'use client';

import { useState } from 'react';
import { CareerPath } from '@/lib/careerPaths';

interface ResumeGeneratorProps {
  diagnosticId: string;
  topCareer: CareerPath;
}

export default function ResumeGenerator({ diagnosticId, topCareer }: ResumeGeneratorProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');

  const handleGenerateResume = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch('/api/resume/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          diagnosticId,
          careerId: topCareer.id,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to generate resume');
        setLoading(false);
        return;
      }

      setDownloadUrl(data.downloadUrl);
      setSuccess(true);
      setLoading(false);

      // Automatically trigger download
      if (data.downloadUrl) {
        window.location.assign(data.downloadUrl);
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{
        background: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '0.75rem',
      }}>
        <div style={{
          fontSize: '0.95rem',
          color: '#22C55E',
          marginBottom: '0.75rem',
          fontWeight: 600,
        }}>
          ✓ Resume generated successfully!
        </div>
        <a
          href={downloadUrl}
          download
          style={{
            display: 'inline-block',
            padding: '0.5rem 1rem',
            background: 'linear-gradient(135deg, #22C55E, #16A34A)',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          Download Resume
        </a>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '0.75rem' }}>
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '0.75rem',
          marginBottom: '0.75rem',
          color: '#FCA5A5',
          fontSize: '0.875rem',
        }}>
          {error}
        </div>
      )}

      <button
        onClick={handleGenerateResume}
        disabled={loading}
        style={{
          padding: '0.625rem 1.25rem',
          background: loading
            ? '#999'
            : 'linear-gradient(135deg, #D96704, #FF6C37)',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '0.875rem',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        {loading ? (
          <span>
            <span style={{ marginRight: '0.5rem' }}>⏳</span>
            Generating Resume...
          </span>
        ) : (
          <span>
            <span style={{ marginRight: '0.5rem' }}>📄</span>
            Generate Resume for {topCareer.title}
          </span>
        )}
      </button>

      <p style={{
        fontSize: '0.75rem',
        color: '#A8A8A8',
        marginTop: '0.5rem',
      }}>
        Creates a professional resume optimized for {topCareer.title} positions
      </p>
    </div>
  );
}
